import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Form, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaDatabase, FaArrowRight, FaLightbulb, FaRocket, FaFilter, FaSearch, 
  FaTh, FaList, FaCog, FaMemory, FaHdd, FaNetworkWired, 
  FaExternalLinkAlt, FaPhone, FaChevronLeft, FaChevronRight, FaShieldAlt, 
  FaCloud, FaLayerGroup 
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const Storage = () => {
  const { t, lang } = useLanguage(); 
  const [storageRef, storageInView] = useScrollAnimation();

  // Fonction de traduction étendue pour couvrir PowerFlex et Unity
  const translateSpec = (text) => {
    if (!text || lang === 'fr') return text;
    return text
      .replace(/Jusqu'à/g, "Up to")
      .replace(/par nœud/g, "per node")
      .replace(/par paire de nœuds/g, "per node pair")
      .replace(/effective/g, "effective")
      .replace(/nœuds par cluster/g, "nodes per cluster")
      .replace(/jusqu'à/g, "up to")
      .replace(/de cache/g, "cache")
      .replace(/Économique/g, "Economic")
      // Mots PowerFlex
      .replace(/Illimitée/g, "Unlimited")
      .replace(/Dépend du hardware/g, "Depends on hardware")
      .replace(/Mix SSD\/HDD configurable/g, "Configurable SSD/HDD mix")
      // Mots Unity & Badges
      .replace(/Entreprise/g, "Enterprise")
      .replace(/PME/g, "SMB")
      .replace(/Flexible/g, "Flexible");
  };

  const STORAGE_DATA = useMemo(() => ({
    categories: [
      { id: 'all-flash', name: t.storage.categories.af, description: t.storage.categories.afD, icon: <FaRocket /> },
      { id: 'unified-storage', name: t.storage.categories.un, description: t.storage.categories.unD, icon: <FaLayerGroup /> },
      { id: 'scale-out-nas', name: t.storage.categories.nas, description: t.storage.categories.nasD, icon: <FaNetworkWired /> },
      { id: 'software-defined', name: t.storage.categories.sds, description: t.storage.categories.sdsD, icon: <FaCog /> },
      { id: 'backup-protection', name: t.storage.categories.bk, description: t.storage.categories.bkD, icon: <FaShieldAlt /> }
    ],

    storageProducts: [
      {
        id: 1,
        name: 'PowerStore 1200T',
        category: 'all-flash',
        storageType: 'all-flash',
        image: '/images/stockage/powerstore1200t.avif',
        specs: { processors: '4 Intel Xeon CPUs, 40 cores; 2.4 GHz', storageCapacity: 'Jusqu\'à 5.90 PB effective', memory: '384 GB', network: '100 Gb/25GbE , 32 Gb FC', maxDrives: '93 per appliance, 372 per cluster' },
        badge: 'Intelligent'
      },
      {
        id: 2,
        name: 'PowerStore 3200T',
        category: 'all-flash',
        storageType: 'all-flash',
        image: '/images/stockage/powerstore3200t.avif',
        specs: { processors: '4 Intel Xeon CPUs, 64 cores; 2.1 GHz', storageCapacity: 'Jusqu\'à 5.90 PB effective', memory: '768 GB', network: '100 Gb/25GbE , 32 Gb FC', maxDrives: '93 per appliance, 372 per cluster' },
        badge: 'Performance'
      },
      {
        id: 3,
        name: 'PowerStore 9200T',
        category: 'all-flash',
        storageType: 'all-flash',
        image: '/images/stockage/powerstore9200t.avif',
        specs: { processors: '4 Intel Xeon CPUs, 112 cores; 2.2 GHz', storageCapacity: 'Jusqu\'à 5.90 PB effective', memory: '2560 GB', network: '100 Gb/25GbE , 32 Gb FC', maxDrives: '93 per appliance, 372 per cluster' },
        badge: t.storage.card.badgeNew
      },
      {
        id: 4,
        name: 'PowerMax 2500',
        category: 'all-flash',
        storageType: 'enterprise',
        image: '/images/stockage/powermax2500.jpeg',
        specs: { processors: 'Jusqu\'à 4 Intel Xeon CPUs, 2.8 GHz, 64 cores par paire de nœuds', storageCapacity: '15.36 TBu - 8 PBe par array', memory: '896 GB jusqu\'à 15.36 TB de cache', network: '2 ou 4 nœuds par array', maxDrives: 'NVMe: 3.84TB, 7.68TB, 15.36TB, 30.72TB' },
        badge: 'Mission Critical'
      },
      {
        id: 5,
        name: 'PowerMax 8500',
        category: 'all-flash',
        storageType: 'enterprise',
        image: '/images/stockage/powermax8500.avif',
        specs: { processors: 'Jusqu\'à 4 Intel Xeon CPUs, 3.9 GHz, 72 cores par paire de nœuds', storageCapacity: '15.36 TBu - 18 PBe par array', memory: '1.792 TB jusqu\'à 45.056 TB de cache', network: '2 jusqu\'à 16 nœuds par array', maxDrives: 'NVMe: 3.84TB, 7.68TB, 15.36TB, 30.73TB' },
        badge: 'Enterprise'
      },
      {
        id: 6,
        name: 'PowerScale F210',
        category: 'scale-out-nas',
        storageType: 'nas',
        image: '/images/stockage/powerscalef210.avif',
        specs: { processors: '3 à 252 nœuds par cluster', storageCapacity: '7.7TB - 61TB par nœud | 23TB - 15PB par cluster', memory: '128 GB', network: 'NVMe SSD (TLC, QLC)', maxDrives: '4 SSD drives par nœud' },
        badge: 'Économique'
      },
      {
        id: 7,
        name: 'PowerScale F710',
        category: 'scale-out-nas',
        storageType: 'nas',
        image: '/images/stockage/powerscalef710.avif',
        specs: { processors: '3 à 252 nœuds par cluster', storageCapacity: '38TB - 1.2PB par nœud | 115TB - 307PB par cluster', memory: '512 GB', network: 'NVMe SSD (TLC, QLC)', maxDrives: '10 SSD drives par nœud' },
        badge: 'Balanced'
      },
      {
        id: 8,
        name: 'PowerScale F910',
        category: 'scale-out-nas',
        storageType: 'nas',
        image: '/images/stockage/powerscalef910.avif',
        specs: { processors: '3 à 252 nœuds par cluster', storageCapacity: '92TB - 2.9PB par nœud | 276TB - 737PB par cluster', memory: '512 GB', network: 'NVMe SSD (TLC, QLC)', maxDrives: '24 SSD drives par nœud' },
        badge: 'AI-Ready'
      },
      {
        id: 9,
        name: 'PowerFlex Rack',
        category: 'software-defined',
        storageType: 'hci',
        image: '/images/stockage/powerflex.avif',
        specs: { processors: '2 x Intel Xeon Silver 4314', storageCapacity: 'Jusqu\'à 57.6TB par nœud', memory: '128 GB DDR4', network: '2 x 25GbE', maxDrives: 'Mix SSD/HDD configurable' },
        badge: 'Flexible'
      },
      {
        id: 10,
        name: 'PowerFlex Software',
        category: 'software-defined',
        storageType: 'software',
        image: '/images/stockage/powerflex-software.avif',
        specs: { processors: 'Vendor-agnostic', storageCapacity: 'Illimitée (software-only)', memory: 'Dépend du hardware', network: 'Flexible', maxDrives: 'Vendor-agnostic' },
        badge: 'Software'
      },
      {
        id: 11,
        name: 'Unity XT 380',
        category: 'unified-storage',
        storageType: 'unified',
        image: '/images/stockage/unityxt380.avif',
        specs: { processors: '2 x Intel CPUs, 12 cores; 1.7GHz', storageCapacity: 'Jusqu\'à 2.4PB', memory: '128 GB', network: 'FC, iSCSI, NFS, SMB', maxDrives: '500' },
        badge: 'PME'
      },
      {
        id: 12,
        name: 'Unity XT 680',
        category: 'unified-storage',
        storageType: 'unified',
        image: '/images/stockage/unityxt680.avif',
        specs: { processors: '2 dual-socket Intel CPUs, 48 cores; 2.1GHz', storageCapacity: 'Jusqu\'à 8PB', memory: '384 GB', network: 'FC, iSCSI, NFS, SMB', maxDrives: '1000' },
        badge: 'Entreprise'
      }
    ]
  }), [t, lang]);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStorageType, setSelectedStorageType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const itemsPerPage = 9;

  const filteredStorage = useMemo(() => {
    return STORAGE_DATA.storageProducts.filter(p => {
      const catMatch = selectedCategory === 'all' || p.category === selectedCategory;
      const typeMatch = selectedStorageType === 'all' || p.storageType === selectedStorageType;
      const searchMatch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      return catMatch && typeMatch && searchMatch;
    });
  }, [selectedCategory, selectedStorageType, searchTerm, STORAGE_DATA]);

  const totalPages = Math.ceil(filteredStorage.length / itemsPerPage);
  const paginatedStorage = filteredStorage.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.storage.seo.title}</title>
        <meta name="description" content={t.storage.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/stockage" />
        
        {/* Open Graph (Réseaux sociaux) */}
        <meta property="og:title" content={t.storage.seo.title} />
        <meta property="og:description" content={t.storage.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/stockage/powerstore1200t.avif" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Hero */}
      <section style={{
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
          padding: '6rem 0 4rem 0', position: 'relative', overflow: 'hidden'
      }}>
        <Container>
          <Row className="align-items-center text-white">
            <Col lg={8}>
              <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
                <Badge bg="danger" className="mb-3" style={{ padding: '0.5rem 1rem', borderRadius: '20px' }}>
                  <FaDatabase className="me-2" /> {t.storage.hero.badge}
                </Badge>
                <h1 style={{ fontSize: '3.5rem', fontWeight: 700 }} dangerouslySetInnerHTML={{ __html: t.storage.hero.title }} />
                <p style={{ fontSize: '1.3rem', opacity: 0.9 }}>{t.storage.hero.subtitle}</p>
                <div className="d-flex gap-3 mt-4">
                  <Button variant="danger" size="lg" style={{ borderRadius: '16px' }} onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })}>{t.storage.hero.btnExplore}</Button>
                  <Button variant="outline-light" size="lg" style={{ borderRadius: '16px' }}>{t.storage.hero.btnGuide}</Button>
                </div>
              </motion.div>
            </Col>
            <Col lg={4} className="text-center">
               <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '2rem', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <h4 className="fw-bold">{t.storage.hero.certified}</h4>
                <p className="small opacity-75">{t.storage.hero.idc}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Section */}
      <section id="solutions-section" className="py-5" ref={storageRef}>
        <Container>
          <Row>
            {/* Sidebar */}
            <Col lg={3}>
              <div className="bg-white p-4 shadow-sm" style={{ borderRadius: '20px', position: 'sticky', top: '2rem' }}>
                <div className="mb-4">
                  <label className="fw-bold small mb-2 text-uppercase"><FaSearch className="me-2" />{t.storage.sidebar.search || t.storage.sidebar.searchLabel}</label>
                  <Form.Control type="text" placeholder={t.storage.sidebar.searchPl || t.storage.sidebar.searchPlaceholder} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="mb-4">
                  <label className="fw-bold small mb-2 text-uppercase"><FaFilter className="me-2" />{t.storage.sidebar.category}</label>
                  <div className="d-grid gap-2">
                    <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} size="sm" style={{textAlign: 'left'}} onClick={() => setSelectedCategory('all')}><FaFilter className="me-2" />{t.storage.sidebar.allCats}</Button>
                    {STORAGE_DATA.categories.map(cat => (
                      <Button key={cat.id} variant={selectedCategory === cat.id ? 'danger' : 'outline-secondary'} size="sm" style={{textAlign: 'left'}} onClick={() => setSelectedCategory(cat.id)}>
                        <span className="me-2">{cat.icon}</span> {cat.name}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="text-center p-3 mb-4 bg-light rounded small">
                   <span className="fw-bold text-danger">{filteredStorage.length}</span> {t.storage.sidebar.found}
                </div>
                <div className="d-grid gap-2">
                    <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('grid')}><FaTh className="me-2"/>{t.storage.sidebar.grid}</Button>
                    <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('list')}><FaList className="me-2"/>{t.storage.sidebar.list}</Button>
                </div>
              </div>
            </Col>

            {/* Produits */}
            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedStorage.map((p) => (
                    <Col lg={4} md={6} key={p.id}>
                      <Card className="h-100 border-0 shadow-sm" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                        <div style={{ height: '180px', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          {/* Badge Traduit (ex: PME -> SMB) */}
                          <Badge bg="primary" style={{ position: 'absolute', top: '1rem', left: '1rem' }}>{translateSpec(p.badge)}</Badge>
                          <img src={p.image} alt={p.name} style={{ width: '85%', objectFit: 'contain' }} />
                        </div>
                        <Card.Body className="p-4">
                          <h5 className="fw-bold mb-1">{p.name}</h5>
                          <p className="text-muted small mb-3">{STORAGE_DATA.categories.find(c => c.id === p.category)?.description}</p>
                          
                          <div className="bg-light p-3 rounded mb-3 border">
                            <Row className="g-0 text-center">
                                <Col xs={6} className="border-end">
                                    <div className="small text-muted text-uppercase" style={{fontSize: '0.65rem'}}>{t.storage.card.capacity}</div>
                                    <div className="fw-bold small">{translateSpec(p.specs.storageCapacity)}</div>
                                </Col>
                                <Col xs={6}>
                                    <div className="small text-muted text-uppercase" style={{fontSize: '0.65rem'}}>{t.storage.card.processors}</div>
                                    <div className="fw-bold small">{translateSpec(p.specs.processors)}</div>
                                </Col>
                            </Row>
                          </div>

                          <h6 className="fw-bold small text-uppercase mb-2">{t.storage.card.specs}</h6>
                          <div className="small text-muted">
                            {/* Mémoire Traduite (ex: Dépend du hardware -> Depends on hardware) */}
                            <div className="mb-1"><FaMemory className="me-2 text-primary"/><strong>{t.storage.card.memory}:</strong> {translateSpec(p.specs.memory)}</div>
                            <div className="mb-1"><FaNetworkWired className="me-2 text-success"/><strong>{t.storage.card.network}:</strong> {translateSpec(p.specs.network)}</div>
                            <div className="mb-1"><FaHdd className="me-2 text-warning"/><strong>{t.storage.card.disks}:</strong> {translateSpec(p.specs.maxDrives)}</div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                /* Vue liste */
                <div className="d-grid gap-3">
                   {paginatedStorage.map((p) => (
                       <Card key={p.id} className="border-0 shadow-sm p-3">
                           <Row className="align-items-center">
                               <Col md={3}><img src={p.image} alt={p.name} className="img-fluid" /></Col>
                               <Col md={9}>
                                   <h5 className="fw-bold">{p.name} <Badge bg="primary" className="ms-2">{translateSpec(p.badge)}</Badge></h5>
                                   <p className="text-muted small">{translateSpec(p.specs.storageCapacity)}</p>
                               </Col>
                           </Row>
                       </Card>
                   ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5 gap-2">
                    <Button variant="outline-secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><FaChevronLeft /></Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button key={page} variant={currentPage === page ? 'danger' : 'outline-secondary'} onClick={() => handlePageChange(page)}>{page}</Button>
                    ))}
                    <Button variant="outline-secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><FaChevronRight /></Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Storage;